import { Link } from 'react-router-dom';
import { useRef, useLayoutEffect } from 'react';
import { useHeaderUtilities } from './_Hooks/useHeaderUtilities';
import { LAYOUTS } from '../../../../../_CORE/_RuntimeConfig/_Config/_Layouts.js';

const BLOCK = 'LStudio__app_utility';

// 🔥 Cuántos items entran en el panel antes de mandar a "Ver todos".
export const UTILITY_MAX_ITEMS = LAYOUTS.SHELL.UTILITY_MAX_ITEMS;

/*
    🔥 CASCARA DE CUALQUIER UTILIDAD

    Botón + badge + panel. Toda la geometría sale de --LS__tpl_*.

    El prop `footer` se dibuja FUERA del área con scroll: los botones
    de acción quedan siempre visibles aunque la lista se desplace.
*/

/*
    🔥 QUE EL PANEL NO SE SALGA DE LA PANTALLA

    ⚠️ El panel se ancla al boton con right: 0 (data-align="end"), asi
       que crece hacia la IZQUIERDA. En la utilidad de mas a la
       izquierda del grupo (el carrito) eso lo mandaba fuera del
       viewport y se veia mordido.

    ⚠️ NO SE ARREGLA CON CSS.

       El panel es position: absolute dentro del header, asi que sus
       coordenadas son relativas al boton. CSS no tiene forma de
       preguntar "cuanto me pase del borde de la ventana". Hay que
       medirlo.

    Se mide despues de pintar y se corre con translateX lo justo para
    que entre. El anclaje original no se toca: solo se desplaza.

    ⚠️ Va en useLayoutEffect y no useEffect: corre ANTES de que el
       navegador pinte, asi no se ve el salto del panel acomodandose.

    ⚠️ Se toca el DOM directo en vez de guardar el desplazamiento en un
       estado. Con estado habria que resetearlo para volver a medir, y
       eso es un render extra que dispara el efecto otra vez: bucle.
*/
const MARGIN_SAFE = LAYOUTS.SHELL.UTILITY_SAFE_MARGIN;

export const UtilityDropdown = ({ id, icon, label, badge = 0, width = 320, align = 'end', footer = null, children }) => {

    const { useOpenId, onToggle } = useHeaderUtilities();

    const IS_OPEN = useOpenId === id;

    const REF__panel = useRef(null);

    useLayoutEffect(() => {

        if (!IS_OPEN) return;

        const onFit = () => {

            const EL = REF__panel.current;
            if (!EL) return;

            // Se limpia primero para medir la posicion real, sin el ajuste anterior.
            EL.style.transform = 'none';

            const RECT = EL.getBoundingClientRect();
            const VIEWPORT = document.documentElement.clientWidth;

            let shift = 0;

            if (RECT.left < MARGIN_SAFE) shift = MARGIN_SAFE - RECT.left;
            else if (RECT.right > VIEWPORT - MARGIN_SAFE) shift = (VIEWPORT - MARGIN_SAFE) - RECT.right;

            EL.style.transform = shift ? `translateX(${Math.round(shift)}px)` : '';
        };

        onFit();

        window.addEventListener('resize', onFit);
        return () => window.removeEventListener('resize', onFit);

    }, [IS_OPEN, width]);

    return (
        <div className={BLOCK}>

            <button type="button" className={`${BLOCK}__button ${IS_OPEN ? `${BLOCK}__button--open` : ''}`}
                aria-label={label} aria-expanded={IS_OPEN} aria-haspopup="true"
                onClick={() => onToggle(id)} >

                {icon}

                {badge > 0 && <span className={`${BLOCK}__badge`}>{badge > 99 ? '99+' : badge}</span>}

            </button>

            {
                IS_OPEN && (
                    <div ref={REF__panel} className={`${BLOCK}__panel`} data-align={align} style={{ width }}>

                        <p className={`${BLOCK}__title`}>{label}</p>

                        <div className={`${BLOCK}__scroll`}>{children}</div>

                        {footer && <div className={`${BLOCK}__footer`}>{footer}</div>}

                    </div>
                )
            }

        </div>
    );
};

// ---------------------------- 🔥 PIEZAS REUTILIZABLES
/*
    🔥 icon ES OPCIONAL

    Se agrego para las banderas del selector de idioma, pero no sabe nada
    de banderas: recibe cualquier nodo. Sin icon, el item se dibuja
    exactamente igual que antes.
*/
export const UtilityItem = ({ icon, title, description, meta, isUnread, isSelected, onClick }) => {

    const IS_CLICKABLE = !!onClick && !isSelected;

    return (
        <div className={`${BLOCK}__item ${icon ? `${BLOCK}__item--with_icon` : ''} ${isUnread ? `${BLOCK}__item--unread` : ''} ${isSelected ? `${BLOCK}__item--selected` : ''} ${IS_CLICKABLE ? `${BLOCK}__item--clickable` : ''}`}
            onClick={IS_CLICKABLE ? onClick : undefined}
            role={IS_CLICKABLE ? 'button' : undefined}
            aria-current={isSelected ? 'true' : undefined} >

            {icon && <span className={`${BLOCK}__item_icon`} aria-hidden="true">{icon}</span>}

            <div className={`${BLOCK}__item_body`}>
                {title && <strong>{title}</strong>}
                {description && <p>{description}</p>}
                {meta && <span>{meta}</span>}
            </div>

        </div>
    );
};

export const UtilityEmpty = ({ icon, title, hint }) => (

    <div className={`${BLOCK}__empty`}>
        {icon && <span className={`${BLOCK}__empty_icon`}>{icon}</span>}
        <p>{title}</p>
        {hint && <small>{hint}</small>}
    </div>
);

export const UtilityAction = ({ children, onClick, isDanger }) => (

    <button type="button" onClick={onClick}
        className={`${BLOCK}__action ${isDanger ? `${BLOCK}__action--danger` : ''}`} >
        {children}
    </button>
);

// 🔥 "Ver todos" — aparece siempre que sobren items, tenga ruta o no.
export const UtilityViewAll = ({ total, max = UTILITY_MAX_ITEMS, onClick, label = '' }) => {

    if (total <= max) return null;

    const HIDDEN = total - max;

    return (
        <button type="button" onClick={onClick ?? undefined} disabled={!onClick}
            className={`${BLOCK}__view_all`} >
            {label?.replace('{{total}}', total).replace('{{hidden}}', HIDDEN)}
        </button>
    );
};

/*
    🔥 UTILIDAD SIN PANEL

    Mismo botón, mismo badge, misma posición que UtilityDropdown,
    pero no abre nada: navega. Sirve para las utilidades que solo
    necesitan mostrar un contador y llevar a su vista completa.
*/
export const UtilityLink = ({ to, icon, label, badge = 0 }) => {

    const { onClose } = useHeaderUtilities();

    if (!to) {
        return (
            <span className={`${BLOCK}__button ${BLOCK}__button--disabled`}
                aria-label={label} title={label} >
                {icon}
                {badge > 0 && <span className={`${BLOCK}__badge`}>{badge > 99 ? '99+' : badge}</span>}
            </span>
        );
    }

    return (
        <Link to={to} className={`${BLOCK}__button`} onClick={onClose}
            aria-label={label} title={label} >
            {icon}
            {badge > 0 && <span className={`${BLOCK}__badge`}>{badge > 99 ? '99+' : badge}</span>}
        </Link>
    );
};
